import { createElement, lazy, type ComponentType, type ReactNode } from "react";

// A lazily loaded component that can also be loaded ahead of rendering
// (ADR-WBC1). React.lazy always suspends on its first render, even when the
// module is already in memory, so a Suspense fallback would replace the
// prerendered splash for a frame. Once `preload()` has resolved, this renders
// the loaded component directly and never suspends.
//
// A failed load is retried once in place after a short pause (a dropped
// connection, a deploy that has not reached this edge yet). If that fails
// too, the error reaches the app's error boundary (src/boot/AppErrorBoundary).
// The rejection stays cached, as React.lazy's does, so React's own re-render
// cannot turn it into an endless retry loop; the boundary's "Try again" calls
// resetFailedLoads() so the next render starts a fresh attempt.

export type PreloadableComponent<C> = C & {
  /** Starts (or joins) the load; resolves once rendering will not suspend. */
  preload: () => Promise<void>;
};

type AnyProps = Record<string, unknown>;
type Loaded = { default: ComponentType<AnyProps> };

const RETRY_DELAY_MS = 600;

const failedLoads = new Set<() => void>();

/** Lets every load that failed start afresh on its next render. */
export function resetFailedLoads(): void {
  const resets = [...failedLoads];
  failedLoads.clear();
  for (const reset of resets) reset();
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function preloadable<C extends (props: never) => ReactNode>(
  load: () => Promise<{ default: C }>,
  retryDelayMs = RETRY_DELAY_MS,
  /**
   * The module once another caller's load of the same chunk has finished
   * (src/lib/console-loaders.ts keeps one). Without it a prefetch made before
   * the first render would not stop React.lazy suspending, which holds the
   * content back for its 300 ms minimum fallback time (P-07).
   */
  peek?: () => { default: C } | undefined
): PreloadableComponent<C> {
  let loaded: ComponentType<AnyProps> | null = null;
  let pending: Promise<Loaded> | null = null;

  const start = (): Promise<Loaded> => {
    pending ??= load()
      .catch(() => wait(retryDelayMs).then(load))
      .then(
        (module) => {
          const component = module.default as unknown as ComponentType<AnyProps>;
          loaded = component;
          return { default: component };
        },
        (error: unknown) => {
          // preload() may try again at once; rendering waits for a reset.
          pending = null;
          failedLoads.add(() => {
            Lazy = lazy(start);
          });
          throw error;
        }
      );
    return pending;
  };
  let Lazy = lazy(start);

  function Preloadable(props: AnyProps) {
    const ready = loaded ?? (peek?.()?.default as unknown as ComponentType<AnyProps> | undefined);
    return ready ? createElement(ready, props) : createElement(Lazy, props);
  }
  return Object.assign(Preloadable, { preload: () => start().then(() => undefined) }) as unknown as PreloadableComponent<C>;
}
